import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { deliveryRunAPI } from '../../services/api';
import './DailyRunsheet.css';
import { sortByLoadingPosition } from '../../utils/deliveryOccupancy';
import { todayKey } from '../../utils/date';

const STARTING_ADDRESS = process.env.REACT_APP_GOOGLE_MAPS_STARTING_ADDRESS || '1319 Te Aroha Gordon Road, Manawaru 3391';

// Helper to add minutes to a time string (HH:MM format)
function addMinutesToTime(timeStr, minutesToAdd) {
  if (!timeStr) return null;
  const match = String(timeStr).match(/(\d{2}):(\d{2})/);
  if (!match) return null;
  const hours = parseInt(match[1], 10);
  const minutes = parseInt(match[2], 10);
  const totalMinutes = hours * 60 + minutes + minutesToAdd;
  const newHours = Math.floor(totalMinutes / 60) % 24;
  const newMinutes = totalMinutes % 60;
  return `${String(newHours).padStart(2, '0')}:${String(newMinutes).padStart(2, '0')}`;
}

// Format time with AM/PM
function formatTimeWithPeriod(timeStr) {
  if (!timeStr) return '';
  const match = String(timeStr).match(/(\d{2}):(\d{2})/);
  if (!match) return '';
  const hours = parseInt(match[1], 10);
  const minutes = match[2];
  const period = hours >= 12 ? 'PM' : 'AM';
  const displayHours = hours % 12 || 12;
  return `${displayHours}:${minutes} ${period}`;
}

// Format date for display
function formatDate(dateStr) {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-NZ', {
    weekday: 'short',
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  });
}

// Format date for display (short)
function formatDateShort(dateStr) {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-NZ', {
    day: '2-digit',
    month: 'short',
  });
}

// Check if order has any items with size 0 (excluding a specific item)
// Uses order.orderLines from API which includes all order lines for the order
function getSizeZeroOtherOrderItemLetter(item) {
  const order = item.order;
  if (!order || !order.order_lines) return null;
  
  // Find other order lines with size 0 (excluding current item's order line)
  const otherItemsWithSizeZero = order.order_lines.filter(
    ol => ol.size === 0 && ol.id !== item.order_line?.id
  );
  
  if (otherItemsWithSizeZero.length > 0) {
    return otherItemsWithSizeZero[0].product?.code?.[0]?.toUpperCase() || null;
  }
  return null;
}

function DailyRunsheet() {
  const navigate = useNavigate();
  const printRef = useRef(null);
  const [selectedDate, setSelectedDate] = useState('');
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Initialize with date from URL or today's date
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const dateParam = urlParams.get('date');
    
    console.log('[DailyRunsheet] URL date param:', dateParam);
    
    if (dateParam) {
      console.log('[DailyRunsheet] Using date from URL:', dateParam);
      setSelectedDate(dateParam);
    } else {
      const today = todayKey();
      console.log('[DailyRunsheet] No URL date, using today:', today);
      setSelectedDate(today);
    }
  }, []);

  // Fetch runs when date changes
  useEffect(() => {
    if (selectedDate) {
      fetchRunsForDate();
    }
  }, [selectedDate]);

  const fetchRunsForDate = async () => {
    setLoading(true);
    setError(null);
    try {
      // Fetch all runs for the date
      const runsData = await deliveryRunAPI.getByDate(selectedDate);

      // DEFECT D (§3-D): Planning runs used to be dropped here, so a run
      // that existed but was not yet confirmed was simply ABSENT from the
      // sheet - the client reported "a whole run missing from the
      // sheet". Planning runs are shown, flagged as not confirmed, so
      // "never created" and "not confirmed" are distinguishable.
      const allRuns = runsData || [];
      const notConfirmed = allRuns.filter(run => run.status < 2).length;

      // Log the raw count so support can say "3 runs returned, 1 not yet
      // confirmed" rather than guessing.
      console.log(
        `[DailyRunsheet] ${allRuns.length} run(s) returned for ${selectedDate}, ` +
        `${notConfirmed} not yet confirmed`,
      );

      setRuns(allRuns);
    } catch (err) {
      console.error('Error fetching delivery runs:', err);
      setError('Failed to load delivery runs');
    } finally {
      setLoading(false);
    }
  };

  // Calculate ETA range for a run
  const calculateEtaRange = (run) => {
    if (!run.start_time) return { earliest: '', latest: '' };
    
    // Estimate ~15 min per stop + travel time
    const deliveryCount = run.deliveries?.length || 0;
    const stopTimeMinutes = deliveryCount * 15;
    const travelTimeMinutes = 2 * 30; // ~30km avg
    const totalDurationMinutes = stopTimeMinutes + travelTimeMinutes;
    
    const etaEarliest = addMinutesToTime(run.start_time, totalDurationMinutes);
    const etaLatest = addMinutesToTime(run.start_time, totalDurationMinutes + 60);
    
    return {
      earliest: formatTimeWithPeriod(etaEarliest),
      latest: formatTimeWithPeriod(etaLatest)
    };
  };

  // Print handler
  const handlePrint = () => {
    window.print();
  };

  // Go back
  const handleBack = () => {
    navigate(`/deliveries/planning?date=${selectedDate}`);
  };

  if (loading) {
    return (
      <div className="runsheet-loading">
        <div className="spinner"></div>
        <p>Loading runsheet...</p>
      </div>
    );
  }

  return (
    <div className="daily-runsheet-page runsheet">
      {/* Header */}
      <div className="runsheet-header no-print">
        <div className="runsheet-title">
          <h1>DAILY RUNSHEET</h1>
          <span className="run-date">{formatDate(selectedDate)}</span>
        </div>
        <div className="runsheet-controls">
          <div className="form-group inline">
            <label htmlFor="date-picker">Date:</label>
            <input
              type="date"
              id="date-picker"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="form-control"
            />
          </div>
          <button className="btn btn-secondary" onClick={handleBack}>
            ← Back
          </button>
          <button className="btn btn-primary" onClick={handlePrint}>
            🖨️ Print
          </button>
        </div>
      </div>

      {/* Print-only header that repeats on each page */}
      <div className="print-header">
        <div className="print-header-left">
          <span className="print-title">DAILY RUNSHEET</span>
          <span className="print-date">{formatDate(selectedDate)}</span>
        </div>
        <div className="print-header-right">
          <span className="print-page-number"></span>
        </div>
      </div>

      {/* Runsheet content */}
      <div className="runsheet-content" ref={printRef}>
        {error && (
          <div className="error-message">{error}</div>
        )}

        {runs.length === 0 ? (
          <div className="empty-runs">
            <p>No runs found for {formatDate(selectedDate)}</p>
            <p className="empty-runs-hint">
              A run that has not been created yet will not appear here. A run
              that exists but is not yet confirmed is shown with a
              &ldquo;not confirmed&rdquo; banner.
            </p>
          </div>
        ) : (
          <div className="runs-container">
            {runs.map((run) => {
              // Build position map for truck diagram
              // Map of position -> item info, plus track spans
              const positionMap = {};
              const deliverySpans = []; // Track which positions are covered by spans
              
              (run.deliveries || []).forEach((delivery) => {
                delivery.items?.forEach((item) => {
                  const pos = item.loading_position;
                  const size = item.order_line?.size || 1;
                  const qty = item.order_line?.qty || 1;
                  const span = size * qty;
                  
                  positionMap[pos] = {
                    productCode: item.order_line?.product?.code || '?',
                    qty: qty,
                    cubes: span,
                    span: span,
                    deliveryId: delivery.id,
                    order: delivery.order,
                    orderLineId: item.order_line?.id,
                    id: item.id,
                    size: size,
                  };
                  
                  // Mark all positions covered by this span
                  for (let i = 0; i < span; i++) {
                    deliverySpans.push(pos + i);
                  }
                });
              });

              // Sort deliveries by position (descending - highest first).
              // The shared helper matches the API's ordering, which is the
              // order the note keys (N1, N2, ...) are assigned in.
              const sortedDeliveries = sortByLoadingPosition(run.deliveries);

              const etaRange = calculateEtaRange(run);
              // Positions 13 at top, 1 at bottom (descending order - reverse display)
              const truckPositions = run.number_of_positions >= 13 ? [13,12,11,10,9,8,7,6,5,4,3,2,1] : [7,6,5,4,3,2,1];

              return (
                <div key={run.id} className="run-sheet">
                  {/* Run header */}
                  <div className="run-header">
                    <div className="run-title">
                      <span className="run-code">{run.code}</span>
                      <span className="run-start">{formatTimeWithPeriod(run.start_time)} - {run.description}</span>
                      {run.status < 2 && (
                        <span className="run-not-confirmed">
                          {run.status_label || 'Planning'} &mdash; not confirmed
                        </span>
                      )}
                    </div>
                    <div className="run-meta">
                      <span className="eta-badge">
                        ETA: {etaRange.earliest} — {etaRange.latest}
                      </span>
                      <span className="stop-count">
                        {sortedDeliveries.length} stop{sortedDeliveries.length !== 1 ? 's' : ''}
                      </span>
                    </div>
                  </div>

                  {/* Two-column layout per run */}
                  <div className="run-body">
                    {/* Left: Itinerary compact */}
                    <div className="run-itinerary">
                      <table className="itinerary-table">
                        <thead>
                          <tr>
                            <th>#</th>
                            <th>Order</th>
                            <th>Product</th>
                            <th>Qty</th>
                            <th>Address</th>
                            <th>Customer</th>
                            <th>Phone</th>
                            <th>Paid</th>
                            <th>ETA</th>
                            <th>Prev</th>
                          </tr>
                        </thead>
                        <tbody>
                          {sortedDeliveries.map((delivery, idx) => {
                            const order = delivery.order || {};
                            const customer = delivery.customer || {};
                            const items = delivery.items || [];
                            const noteKey = order.note_key;
                            const maxPos = Math.max(...items.map(i => i.loading_position));
                            const orderTotal = order.order_total;
                            
                            // Combine product codes and quantities
                            const productStr = items.map(item => {
                              const code = item.order_line?.product?.code || '?';
                              const qty = item.order_line?.qty || 0;
                              return `${code}×${qty}`;
                            }).join(', ');

                            // Format ETA range for this delivery
                            const deliveryEta = (delivery.eta_start && delivery.eta_end)
                              ? `${formatTimeWithPeriod(delivery.eta_start)} - ${formatTimeWithPeriod(delivery.eta_end)}`
                              : null;

                            return (
                              <tr key={delivery.id}>
                                <td className="seq-cell">{idx + 1}</td>
                                <td className="order-cell">
                                  <button 
                                    className="order-link"
                                    onClick={() => window.open(`/orders/view/${order.id}`, '_blank')}
                                  >
                                    {order.order_external_reference || order.id}
                                  </button>
                                  {noteKey && <span className="note-ref">{noteKey}</span>}
                                </td>
                                <td className="products-cell">{items.map(item => item.order_line?.product?.code).join(', ') || '-'}</td>
                                <td className="qty-cell">x {items.map(item => item.order_line?.qty).join(', x ') || '-'}</td>
                                <td className="address-cell">
                                  {order.delivery_address || customer.delivery_address || '-'}
                                </td>
                                <td className="customer-name-cell">
                                  <div className="customer-name">{customer.fullname || '-'}</div>
                                </td>
                                <td className="customer-phone-cell">
                                  <div className="customer-phone">{customer.phone || '-'}</div>
                                </td>
                                <td className="paid-cell">
                                  {order.payment_received ? (
                                    <span className="paid-yes">YES</span>
                                  ) : (
                                    <span className="paid-no">
                                      {order.payment_method || 'OWE'}
                                      {orderTotal != null && (
                                        <span className="amount"> ${orderTotal.toFixed(0)}</span>
                                      )}
                                    </span>
                                  )}
                                </td>
                                <td className="eta-cell">
                                  {deliveryEta && <span className="eta-range">{deliveryEta}</span>}
                                </td>
                                <td className="prev-cell">
                                  {order.is_previous_customer ? 'Y' : ''}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>

                      {/* Notes - rendered from the API's own keys.
                          This used to re-derive the key from the position
                          rank (`N${idx + 1}`) while the API numbered them
                          by delivery id, so the printed N-numbers and the
                          note list were scrambled against each other and
                          a driver matching N3 to a stop got the wrong
                          note. The API is the only source of truth now. */}
                      {run.notes_key && Object.keys(run.notes_key).length > 0 && (
                        <div className="run-notes">
                          <strong>Notes:</strong>
                          {Object.entries(run.notes_key).map(([noteKey, noteText]) => (
                            <div key={noteKey} className="note-item">
                              <span><strong>{noteKey}:</strong> {noteText}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Right: Truck diagram - vertical compact */}
                    <div className="run-truck">
                      <div className="truck-cab">&nbsp;</div>
                      <div className="truck-positions">
                        {truckPositions.map((pos) => {
                          const item = positionMap[pos];
                          // Check if this position is covered by a span but not the start
                          const isCovered = !item && deliverySpans.includes(pos);
                          console.log(item);
                          if (isCovered) {
                            return null; // Skip positions covered by spans (not the start)
                          }
                          
                          // Check if this order has other items with size 0
                          const sizeZeroBadgeLetter = item ? getSizeZeroOtherOrderItemLetter(item) : null;
                          
                          return (
                            <div 
                              key={pos} 
                              className={`truck-pos ${item ? 'occupied' : 'empty'} ${item?.span > 1 ? 'spans-multiple' : ''}`}
                              style={item?.span > 1 ? { gridRowEnd: `span ${item.span}` } : {}}
                            >
                              <span className="pos-num"></span>
                              {item && (
                                <span className="pos-content">
                                  <span className="pos-order">{item.order?.order_external_reference || item.order?.id}</span>
                                  <span className="pos-product">{item.productCode}×{item.cubes} cube{item.cubes !== 1 ? 's' : ''}</span>
                                  {sizeZeroBadgeLetter && (
                                    <span className="size-badge">{sizeZeroBadgeLetter}</span>
                                  )}
                                </span>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="runsheet-footer">
        <span>Firewood Factory Daily Runsheet</span>
        <span>Generated: {new Date().toLocaleString('en-NZ')}</span>
      </div>
    </div>
  );
}

export default DailyRunsheet;
